import React, { JSX, useEffect, useMemo, useState } from "react";
import { ButtonStyle } from "components/buttons/button/button";
import { Modal, ModalAction } from "components/modal/modal";
import { t } from "utilities/localization/t";

import { AenProviderFeeRecord } from "models/view-models/aen-resources/aen-provider-fee-record";
import { AenProviderFeeForm } from "./aen-provider-fee-modal/aen-provider-fee-form";
// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AenProviderFeeModalProps {
    aenProviderFee?: AenProviderFeeRecord;
    editing?: boolean;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    saveAenProviderFee: (aenProviderFee: AenProviderFeeRecord) => void;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AenProviderFeeModal: React.FC<AenProviderFeeModalProps> = (
    props: AenProviderFeeModalProps
): JSX.Element => {
    let initialResource = useMemo(
        () => props.aenProviderFee ?? new AenProviderFeeRecord({}),
        [props.aenProviderFee]
    );
    const [aenProviderFee, setAenProviderFee] = useState(initialResource);
    const [dirty, setDirty] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [disableAddButton, setDisableAddButton] = useState(false);  

    useEffect(() => {
        setDisableAddButton(false);
        setAenProviderFee(initialResource);
    }, [initialResource]);

    const modalActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalClose(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: props.aenProviderFee?.id ? t("saveProviderFee") : t("addProviderFee"),
            disabled:
                disableAddButton ||
                aenProviderFee?.amount == null ||
                aenProviderFee?.amount === 0 ||
                aenProviderFee?.year === undefined ||
                aenProviderFee?.deadline === undefined,
            onClick: () => onAddProviderFeeButtonClick(),
            style: ButtonStyle.Primary,
        },
    ];

    const confirmationActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => setShowConfirmationModal(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("confirm"),
            onClick: () => {
                resetForm();
                props.setOpen(false);
                setShowConfirmationModal(false);
            },
            style: ButtonStyle.Destructive,
        },
    ];

    const resetForm = (): void => {
        setDirty(false);
        setAenProviderFee(initialResource);
    };

    const onModalClose = (): void => {
        if (dirty) {
            setShowConfirmationModal(true);
        } else {
            props.setOpen(false);
        }
    };

    const onAddProviderFeeButtonClick = async (): Promise<void> => {
        if (disableAddButton) {
            return;
        }
        setDisableAddButton(true);
        props.saveAenProviderFee(aenProviderFee);
        setAenProviderFee(aenProviderFee);
        setDirty(false);
        props.setOpen(false);
    };

    return (
        <Modal
            isOpen={props.open}
            onModalClose={onModalClose}
            title={props.editing ? t("updateProviderFee") : t("addProviderFee")}
            actions={modalActionArray}
            modalStyle={""}>
            <AenProviderFeeForm 
                aenProviderFee={aenProviderFee} 
                setAenProviderFee={setAenProviderFee} 
                setDirty={setDirty} 
                />
            <Modal
                isOpen={showConfirmationModal}
                onModalClose={() => {}}
                actions={confirmationActionArray}
                modalStyle={"-inverted"}>
                {t("youHaveUnsavedChanges")}
                <br></br> {t("areYouSureYouWantToExit")}
            </Modal>
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AenProviderFeeModal };

// #endregion Exports
